import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { AppConfigurationService } from './app-configuration.service';
import { Observable, Subject } from 'rxjs';
import { TenantState } from 'src/app/store/state/tenant.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { TenantUser } from 'src/app/models/tenantUser';
import { Tenant } from 'src/app/models/tenant';
import { Select } from '@ngxs/store';
import { PageViewGate } from './page-view-gate';
declare var gtag: any;

/** The two globals the Google tag snippet installs on `window`. */
interface GtagWindow extends Window {
  dataLayer?: any[];
  gtag?: (...args: any[]) => void;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements OnDestroy {
  @Select(TenantState.getCurrentTenant) getCurrentTenant$: Observable<Tenant>;
  @Select(TenantSessionState.getCurrentUser) getCurrentUser$: Observable<TenantUser>;
  private googleAnalyticsId: string;
  private renderer2: Renderer2;
  private scriptsLoaded = false;
  private scriptFailed = false;
  private readonly gate = new PageViewGate();
  private destroy$ = new Subject();

  constructor(
    private rendererFactory2: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private readonly appConfigurationService: AppConfigurationService) {
    this.renderer2 = this.rendererFactory2.createRenderer(null, null);
  }

  init(): void {
    if (!this.scriptsLoaded) {
      this.appConfigurationService.settings$.subscribe(settings => {
        this.googleAnalyticsId = settings.analyticsId;
        if (this.googleAnalyticsId) {
          this.addGoogleScript();
          // Tenant before user, so a user is tied to the tenant that was current when it loaded.
          this.getCurrentTenant$
            .pipe(takeUntil(this.destroy$))
            .subscribe((tenant: Tenant) => {
              this.gate.tenantChanged(tenant?.externalId);
              this.sendPageViews();
            });
          this.getCurrentUser$
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: TenantUser) => {
              this.gate.userChanged(user?.id);
              this.sendPageViews();
            });
          // The first NavigationEnd can come before the settings, when no page view is queued yet.
          if (this.router.navigated) {
            this.gate.navigated(this.router.url, this.routeTenantId());
            this.sendPageViews();
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private addGoogleScript(): void {
    const script: HTMLScriptElement = this.renderer2.createElement('script');
    script.type = 'text/javascript';
    script.onload = this.addInitScript.bind(this);
    script.onerror = this.stopTracking.bind(this);
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAnalyticsId}`;
    script.text = '';
    this.renderer2.appendChild(this.document.body, script);
  }

  // PT-001: this used to append a <script> element with an inline body, which a
  // Content-Security-Policy can only permit by allowing script-src 'unsafe-inline' -
  // and that allowance would defeat most of the point of having a policy. The snippet
  // does nothing that needs to be a script tag, so it is done directly instead.
  private addInitScript(): void {
    const win: GtagWindow | null = this.document.defaultView;
    if (!win) {
      return;
    }
    const dataLayer = win.dataLayer || (win.dataLayer = []);
    // Deliberately a function expression, not an arrow function: gtag.js consumes the
    // Arguments object pushed here, and arrow functions have no `arguments`.
    win.gtag = function () {
      dataLayer.push(arguments);
    };
    win.gtag('js', new Date());
    this.scriptsLoaded = true;
    this.sendPageViews();
  }

  trackPageViews(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      filter((evt) => evt instanceof NavigationEnd),
      filter(() => !!this.googleAnalyticsId && !this.scriptFailed),
      tap((event: NavigationEnd) => {
        this.gate.navigated(event.urlAfterRedirects, this.routeTenantId());
        this.sendPageViews();
      }),
    );
  }

  // A blocked or failed Google tag never sends, so held views would only accumulate.
  private stopTracking(): void {
    this.scriptFailed = true;
    this.gate.clear();
  }

  private sendPageViews(): void {
    if (!this.googleAnalyticsId || !this.scriptsLoaded) {
      return;
    }
    const views = this.gate.take();
    if (views.length === 0) {
      return;
    }
    const settings = this.appConfigurationService.settings;
    gtag('set', {
      custom_map: {
        dimension1: settings.analyticsUserIdDimension,
        dimension2: settings.analyticsTenantIdDimension
      }
    });
    gtag('config', this.googleAnalyticsId, { send_page_view: false });
    for (const view of views) {
      // An explicit page_location keeps a held view on its own page, not the page shown when it is sent.
      gtag('event', 'page_view', {
        page_location: this.document.location.origin + view.url,
        [settings.analyticsUserIdDimension]: view.userId,
        [settings.analyticsTenantIdDimension]: view.tenantId
      });
    }
  }

  private routeTenantId(): string | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const tenantId = route.params?.['tenantId'];
      if (tenantId) {
        return tenantId;
      }
      route = route.firstChild;
    }
    return null;
  }
}
